import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps, styled, tooltipClasses } from '@mui/material';
import React from 'react';

import { bodySmall, colorSystem } from '../../theme';

export type TooltipVariant = 'dark' | 'white';

export type TooltipProps = Omit<MuiTooltipProps, 'variant' | 'title'> & {
    variant?: TooltipVariant;
    title: NonNullable<React.ReactNode>;
    disableMaxWidth?: boolean;
};

const defaultStyles = { ...bodySmall };

type PopperSlotProps = NonNullable<TooltipProps['slotProps']>['popper'];
type PopperSlotPropsObject = Exclude<PopperSlotProps, Function | undefined>;

function stopClicks(popperProps: PopperSlotPropsObject | undefined): PopperSlotPropsObject {
    return {
        ...popperProps,
        onClick: (event) => {
            popperProps?.onClick?.(event);
            event.stopPropagation();
        },
    };
}

const DarkTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        ...defaultStyles,
    },
});

const WhiteTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        color: colorSystem.neutral[7],
        backgroundColor: colorSystem.neutral.white,
        boxShadow: `0px 2px 8px rgba(59, 58, 66, 0.12)`,
        ...defaultStyles,
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: colorSystem.neutral.white,
    },
});

const DarkToolTipWithNoMaxWidth = styled(({ className, ...props }: TooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        ...defaultStyles,
        maxWidth: 'none',
    },
});

const WhiteToolTipWithNoMaxWidth = styled(({ className, ...props }: TooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        ...defaultStyles,
        color: colorSystem.neutral[7],
        backgroundColor: colorSystem.neutral.white,
        boxShadow: `0px 2px 8px rgba(59, 58, 66, 0.12)`,
        maxWidth: 'none',
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: colorSystem.neutral.white,
    },
});

/** Stops clicks at the popper: portal events would otherwise bubble to React ancestors such as a table row. */
export const Tooltip = React.forwardRef<React.Ref<HTMLDivElement>, TooltipProps>(
    ({ children, title, variant = 'dark', disableMaxWidth, slotProps: callerSlotProps, ...props }, ref) => {
        const callerPopper = callerSlotProps?.popper;
        const slotProps: TooltipProps['slotProps'] = {
            ...callerSlotProps,
            popper:
                typeof callerPopper === 'function'
                    ? (ownerState) => stopClicks(callerPopper(ownerState))
                    : stopClicks(callerPopper),
        };

        if (disableMaxWidth) {
            switch (variant) {
                case 'dark':
                    return (
                        <DarkToolTipWithNoMaxWidth ref={ref} title={title} {...props} slotProps={slotProps}>
                            {children}
                        </DarkToolTipWithNoMaxWidth>
                    );
                case 'white':
                    return (
                        <WhiteToolTipWithNoMaxWidth ref={ref} title={title} {...props} slotProps={slotProps}>
                            {children}
                        </WhiteToolTipWithNoMaxWidth>
                    );
            }
        } else {
            switch (variant) {
                case 'dark':
                    return (
                        <DarkTooltip ref={ref} title={title} {...props} slotProps={slotProps}>
                            {children}
                        </DarkTooltip>
                    );
                case 'white':
                    return (
                        <WhiteTooltip ref={ref} title={title} arrow={false} {...props} slotProps={slotProps}>
                            {children}
                        </WhiteTooltip>
                    );
            }
        }
    },
);
